/**
 * Event-level purchase gates — aligned with app event details / ticket selection.
 */

import type { EventData, FeeStructure } from '../types';
import { isTicketTierOnSale } from './ticketSaleDates';

export function getEventSaleTimeZone(event: EventData | null | undefined): string {
  const tz = event?.timezone?.trim();
  if (tz) return tz;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function isEventEnded(eventEndDate?: string | null, nowMs: number = Date.now()): boolean {
  if (!eventEndDate?.trim()) return false;
  const endMs = new Date(eventEndDate).getTime();
  return Number.isFinite(endMs) && nowMs > endMs;
}

export function isEventCancelled(event?: EventData | null): boolean {
  const status = (event as { status?: string } | null | undefined)?.status;
  if (!status) return false;
  const s = status.toLowerCase();
  return s === 'cancelled' || s === 'canceled';
}

export function isEventClosedForSales(event?: EventData | null, nowMs: number = Date.now()): boolean {
  if (!event) return false;
  return isEventCancelled(event) || isEventEnded(event.event_end_date, nowMs);
}

export function isFeeStructureOnSale(
  tier: FeeStructure,
  saleTimeZone: string,
  nowMs: number = Date.now(),
): boolean {
  return isTicketTierOnSale(tier.sale_start_date, tier.sale_end_date, nowMs, saleTimeZone);
}

export function isFeeStructurePurchasable(
  tier: FeeStructure,
  saleTimeZone: string,
  eventClosed: boolean,
  nowMs: number = Date.now(),
): boolean {
  if (eventClosed) return false;
  if (tier.available <= 0) return false;
  return isFeeStructureOnSale(tier, saleTimeZone, nowMs);
}
